// File: src/services/api/gedcomCitation.ts
// Citation-data uitbreiding voor GEDCOM SOUR-records (#856, archieven-redesign Fase 6).
//
// Pure helpers — geen side-effects, getest in tests/unit/gedcomCitationRoundtrip.test.ts.
// Aanroeper: src/services/api/gedcom.ts (exportGedcom).
//
// Vorm van de input mirrored op archive_import_log-kolommen (snake_case),
// zodat een rij rechtstreeks uit de DB door deze helper kan worden gehaald.
// Aggregator (Open Archieven / WieWasWie) wordt NOOIT als REPO-record uitgegeven —
// die houdt de originele repository. Aggregator-velden gaan via custom Pecktree
// `_-tags` (per GEDCOM-spec voor non-standard tags).

const GEDCOM_MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Aktetype → GEDCOM `EVENTS_RECORDED` (#1291).
 *
 * Het brontype (`sources.source_type`) verliet het systeem nooit: het SOUR-record
 * kende alleen TITL, AUTH, PUBL, WWW, NOTE en REPO. Een memorie van successie
 * kwam bij de ontvanger binnen als een naamloos bronnetje — de genealoog kon niet
 * meer zien wát voor akte het was.
 *
 * GEDCOM 5.5.1 heeft daar een plek voor: `1 DATA` / `2 EVEN <EVENTS_RECORDED>`,
 * "an enumeration of the different kinds of events that were recorded in a
 * particular source". De waarde is een event-tag, geen vrije tekst.
 *
 * Alleen types die écht een gebeurtenis vastleggen staan hier. Een boek, een
 * krant of een online database legt geen bepaald soort gebeurtenis vast; daar
 * zou elke waarde een verzinsel zijn, en een verkeerde `2 EVEN` is schadelijker
 * dan geen — hij stuurt de lezer op een dwaalspoor. `Notarial` en `Military`
 * missen bewust een waarde: een notariële akte kan van alles bevatten, en
 * militaire dienst heeft in 5.5.1 geen eigen event-tag.
 */
export const SOURCE_TYPE_TO_GEDCOM_EVENT: Readonly<Record<string, string>> = {
  CivilBirth: 'BIRT',
  CivilMarriage: 'MARR',
  CivilDeath: 'DEAT',
  ChurchBaptism: 'BAPM',
  ChurchMarriage: 'MARR',
  ChurchBurial: 'BURI',
  Census: 'CENS',
  PopulationRegister: 'RESI',
  Succession: 'PROB',
};

/**
 * Format an ISO date (YYYY-MM-DD or full ISO timestamp) as a GEDCOM date
 * (DD MMM YYYY, e.g. `15 AUG 1903`). Returns '' for invalid/empty input.
 */
export function formatGedcomDate(iso?: string | null): string {
  if (!iso) return '';
  // Already in GEDCOM format (contains a 3-letter month abbreviation)
  if (/^\d{1,2} [A-Z]{3} \d{4}$/.test(iso)) return iso;
  // ISO YYYY-MM-DD short form: parse explicit (avoid timezone-shift via Date)
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (isoMatch) {
    const y = isoMatch[1];
    const m = parseInt(isoMatch[2], 10);
    const d = parseInt(isoMatch[3], 10);
    if (m >= 1 && m <= 12) return `${d} ${GEDCOM_MONTHS[m - 1]} ${y}`;
  }
  // Fallback: parse via Date (handles full ISO timestamps with Z/offset)
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getUTCDate()} ${GEDCOM_MONTHS[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
}

/**
 * Split a string into chunks of `size` characters. Used for splitting long
 * NOTE-text over GEDCOM CONT-continuations (220 chars per line is convention).
 */
export function chunkText(s: string, size: number): string[] {
  const out: string[] = [];
  if (!s || size <= 0) return out;
  for (let i = 0; i < s.length; i += size) out.push(s.slice(i, i + size));
  return out;
}

/**
 * BR-mutatie shape (zoals opgeslagen in archive_import_log.bevolkingsregister_mutations).
 */
export interface CitationLogMutation {
  date?: string | null;
  type?: string | null;
  description?: string | null;
}

/**
 * Citation-log shape: gemapped 1-op-1 op archive_import_log kolommen + sourId.
 * Optionele sourId mag het SOUR-record xref reflecteren (S1, S2, ...) maar
 * speelt geen rol in welke citation-velden worden uitgegeven.
 */
export interface CitationLog {
  /** Optional, niet gebruikt in line-output maar handig voor debug/log. */
  sourId?: string;

  // Aggregator (Open Archieven / WieWasWie / etc.)
  external_record_id?: string | null;
  aggregator_provider?: string | null;
  aggregator_record_url?: string | null;
  aggregator_consulted_at?: string | null;
  transcription_layer?: string | null;

  // BR-folio
  archief_toegangsnummer?: string | null;
  inventarisnummer?: string | null;
  folio_side?: string | null;
  tellingsdatum?: string | null;
  invuldatum?: string | null;
  wijk?: string | null;
  huisnummer?: string | null;
  gezinshoofd_id?: string | null;

  // BR-mutaties
  bevolkingsregister_mutations?: CitationLogMutation[] | null;

  // Proof-argument
  proof_argument?: string | null;
}

/**
 * Bouw de extra GEDCOM-lines voor een SOUR-record op basis van citation-data
 * uit archive_import_log. Geeft alleen de UITBREIDING terug (geen 0 @Sxx@ SOUR
 * header en geen TITL/AUTH/REPO-lines — die worden door de exporter zelf gegenereerd).
 *
 * Lege citation-data levert een lege array op (backwards-compat: bestaande
 * SOUR-records blijven exact gelijk als citation-data ontbreekt).
 *
 * Mapping (zie plan 2026-05-03-archieven-redesign-plan.md, Task 6.2):
 *
 *   Aggregator (apart van repository):
 *     1 _APID <provider>:<external_record_id>
 *     1 _AURL <aggregator_record_url>
 *     1 _ACDA <DD MMM YYYY>
 *     1 _TLAY <transcription_layer>
 *
 *   BR-folio:
 *     1 DATA
 *     2 DATE <tellingsdatum DD MMM YYYY>
 *     2 _RDATE <invuldatum DD MMM YYYY>
 *     1 _FSID <r|v>
 *     1 _WIJK <wijk>
 *     1 _HSNR <huisnummer>
 *
 *   BR-mutaties (per mutation):
 *     1 _MUT <type>
 *     2 DATE <DD MMM YYYY>
 *     2 NOTE <description>
 *
 *   Proof-argument:
 *     1 NOTE <eerste 220 chars>
 *     2 CONT <volgende 220 chars>
 *     ...
 *
 * Aggregator wordt NOOIT als 1 REPO @Rxxx@ uitgegeven — die plek is voor de
 * originele repository (BHIC, RHCL, etc.).
 */
export function buildSourceLines(citationLog: CitationLog): string[] {
  const lines: string[] = [];
  if (!citationLog) return lines;

  // Aggregator (apart van repository)
  if (citationLog.aggregator_provider) {
    // #1714 — zonder de wizard-suffix :p<persoon>:e<event>: _APID is het akte-ID bij de aggregator.
    const ext = (citationLog.external_record_id || '').replace(/:p\d+:e\d+$/, '');
    lines.push(`1 _APID ${citationLog.aggregator_provider}:${ext}`);
    if (citationLog.aggregator_record_url) {
      lines.push(`1 _AURL ${citationLog.aggregator_record_url}`);
    }
    if (citationLog.aggregator_consulted_at) {
      const dt = formatGedcomDate(citationLog.aggregator_consulted_at);
      if (dt) lines.push(`1 _ACDA ${dt}`);
    }
    if (citationLog.transcription_layer) {
      lines.push(`1 _TLAY ${citationLog.transcription_layer}`);
    }
  }

  // BR-folio: DATA-block alleen als we ten minste een telling/invul-datum of toegangsnummer hebben.
  const hasFolioBlock =
    !!citationLog.archief_toegangsnummer || !!citationLog.tellingsdatum || !!citationLog.invuldatum;
  if (hasFolioBlock) {
    lines.push('1 DATA');
    if (citationLog.tellingsdatum) {
      const dt = formatGedcomDate(citationLog.tellingsdatum);
      if (dt) lines.push(`2 DATE ${dt}`);
    }
    if (citationLog.invuldatum) {
      const dt = formatGedcomDate(citationLog.invuldatum);
      if (dt) lines.push(`2 _RDATE ${dt}`);
    }
  }
  if (citationLog.folio_side) {
    lines.push(`1 _FSID ${citationLog.folio_side}`);
  }
  if (citationLog.wijk) {
    lines.push(`1 _WIJK ${citationLog.wijk}`);
  }
  if (citationLog.huisnummer) {
    lines.push(`1 _HSNR ${citationLog.huisnummer}`);
  }

  // BR-mutaties
  const mutations = citationLog.bevolkingsregister_mutations;
  if (Array.isArray(mutations)) {
    for (const m of mutations) {
      if (!m || typeof m !== 'object') continue;
      const type = m.type || 'andere';
      lines.push(`1 _MUT ${type}`);
      if (m.date) {
        const dt = formatGedcomDate(m.date);
        if (dt) lines.push(`2 DATE ${dt}`);
      }
      if (m.description) {
        lines.push(`2 NOTE ${m.description}`);
      }
    }
  }

  // Proof-argument als NOTE met CONT-continuations (>220 chars)
  if (citationLog.proof_argument) {
    const chunks = chunkText(citationLog.proof_argument, 220);
    if (chunks.length > 0) {
      lines.push(`1 NOTE ${chunks[0]}`);
      for (let i = 1; i < chunks.length; i++) {
        lines.push(`2 CONT ${chunks[i]}`);
      }
    }
  }

  return lines;
}
